import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug } from '../lib/cms';
import ReviewSlider from '../components/ReviewSlider';

export default function Teaching() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTeachingData() {
      const page = await fetchPageBySlug('undervisning');
      setPageData(page);
      setLoading(false);
    }
    loadTeachingData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-sand-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-clay-500 border-t-transparent" />
      </div>
    );
  }

  const heroImage = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/billede-til-sangundervisningssiden.jpg';

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={`${heroImage}?width=1920&quality=60&format=auto`}
            alt="Sangundervisning"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-4">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight">
            Sangundervisning & Mindfulness
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base text-white leading-relaxed">
            Professionel og tryg sangundervisning i Odense, der forener krop, sind, stemmeteknik og mindfulness.
          </p>
          <div className="mt-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs uppercase tracking-widest font-semibold text-black hover:bg-sand-50 hover:-translate-y-0.5 transition-all active:scale-98 shadow-sm"
            >
              Book en tid <i className="fa-solid fa-chevron-right text-[10px]" />
            </Link>
          </div>
        </div>
      </section>

      {/* 1. SANGUNDERVISNING & MINDFULNESS I ODENSE C (Lang tekst fra forsiden) */}
      <section className="py-16 md:py-24 bg-white border-b border-sand-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-4xl mx-auto flex flex-col gap-8 text-left">
            <div className="flex flex-col gap-4">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black leading-tight">
                Sangundervisning & Mindfulness i Odense C: Find din stemme, din ro og dit sande udtryk
              </h2>
              <p className="text-lg md:text-xl font-medium text-clay-900 leading-relaxed">
                Til dig, der elsker at synge, og som vil kombinere professionel sangteknik med mental lethed og frigøre dit fulde potentiale.
              </p>
            </div>

            <div className="prose prose-sand prose-headings:font-serif prose-headings:text-black prose-p:text-foreground/80 prose-p:leading-relaxed max-w-none flex flex-col gap-6 text-base md:text-lg">
              <p>
                Kan du synge, og elsker du det kreative rum? Måske spænder præstationspres, tankemylder eller ubevidste spændinger ben for din stemme?
              </p>
              <p>
                Jeg tilbyder et trygt og professionelt rum i hjertet af Odense. Med min solide baggrund som sanger, sangskriver og sangunderviser guider jeg dig igennem både den tekniske og den følelsesmæssige del af sangen. Her kombinerer vi dybdegående sangteknik og kunstnerisk udtryk med mindfulness, så du kan mærke og lande i din krop, slippe kontrollen og synge med større frihed og kapacitet.
              </p>

              <h3 className="font-serif text-2xl font-bold text-black pt-4">
                Hvorfor kombinere sangteknik og mindfulness?
              </h3>
              <p>
                Når du synger, kommer du naturligt i kontakt med din krop og de følelser, du gemmer på. Selvom du er vant til at synge, kan det føles grænseoverskridende at give helt slip. Men når du bevidst bruger mindfulness til at flytte fokus fra hovedet og ned i kroppen, kan præstationsangsten i stedet blive til ekstra energi, der kan bruges konstruktivt. Det er her, dit sande, rå og autentiske udtryk bliver forløst.
              </p>
              <p>
                Undervisningen foregår i den skønne Solsal lige ved Eventyrhaven. Her træder du ind i et lyst og roligt rum, hvor der er højt til loftet, naturskøn udsigt og plads til at trække vejret helt ned i maven.
              </p>

              <p className="font-bold text-black">
                Vi fordyber os i arbejdet med at:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li><strong className="text-black">Mestre sangteknikken:</strong> Få konkrete værktøjer til en sund, stærk og fleksibel stemme.</li>
                <li><strong className="text-black">Synge fra kroppen:</strong> Lær at mestre din vejrtrækning/støtte og bruge din krop som instrument.</li>
                <li><strong className="text-black">Styrke det kunstneriske udtryk:</strong> Formidl fra hjertet og med følelsernes mangfoldighed, så både du selv og dit publikum mærker dig.</li>
                <li><strong className="text-black">Frigøre blokeringer:</strong> Bearbejd fysiske spændinger og mentale barrierer på en tryg og sjov måde.</li>
                <li><strong className="text-black">Åbne op for nye lag:</strong> Gå på opdagelse i din stemmes fulde potentiale og dit kreative råderum.</li>
                <li><strong className="text-black">Finde stor ro:</strong> Oplev sangen som en kilde til fornyet energi, mental lethed og hverdagsglæde.</li>
              </ul>
            </div>

            <div className="pt-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-full bg-cta px-8 py-3.5 text-sm uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all shadow-md active:scale-98"
              >
                Tag din stemme til næste niveau – book din session i Odense C her
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. SANG HOS MIG HANDLER OM DIG, SIND & KROP, SANGTEKNIK & UDTRYK */}
      <section className="py-16 md:py-24 bg-sand-50 border-b border-sand-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-4xl mx-auto flex flex-col gap-12 text-left">
            {/* Introduktion: Sang hos mig handler om dig */}
            <div className="flex flex-col gap-4">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-wide text-black">
                Sang hos mig handler om dig
              </h2>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                Min undervisning handler om dig og tager udgangspunkt i, hvad du specifikt gerne vil arbejde med. Hvilken musik kan du lide, og hvad har du lyst til at synge? Du synger til instrumentale tracks med den originale musik, du holder af. Hvis du selv skriver dine sange, er det også en mulighed at arbejde med dem. Det skal være lærerigt, sjovt, opløftende og kreativt, og der er masser af muligheder.
              </p>
            </div>

            {/* Billede: sangundervisning-live.webp */}
            <div className="overflow-hidden rounded-none shadow-md border border-sand-200 bg-white">
              <img
                src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/sangundervisning-live.webp?width=1200&quality=85&format=auto"
                alt="Sangundervisning live"
                className="w-full h-auto max-h-[500px] object-cover"
              />
            </div>

            {/* Sind & krop samt Sangteknik & udtryk */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-4">
              <div className="flex flex-col gap-4 bg-white p-8 rounded-none border border-sand-200 shadow-sm">
                <h3 className="font-serif text-2xl font-bold text-black">
                  Sind & krop
                </h3>
                <p className="text-base text-foreground/80 leading-relaxed">
                  Vi kommer omkring kropsbevidsthed og bruger enkle, effektive, yogainspirerede fysiske øvelser til åndedræt og afspænding af kroppen. Jeg lægger vægt på at give plads til de følelser, der kommer op, at du rummer dem og udtrykker dem med stemmen. På den måde skaber vi forbindelse mellem sind og krop og finder ro og fokus, så der lige for en stund bliver lukket lidt af for hverdagens travlhed og forpligtelser, og du får fyldt op på glæde- og overskudskontoen.
                </p>
              </div>

              <div className="flex flex-col gap-4 bg-white p-8 rounded-none border border-sand-200 shadow-sm">
                <h3 className="font-serif text-2xl font-bold text-black">
                  Sangteknik & udtryk
                </h3>
                <p className="text-base text-foreground/80 leading-relaxed">
                  Vi arbejder gradvist med grundlæggende sangteknik, hvor vi afhjælper spændinger i kæber, læber, tunge, nakke, hals m.m., så du synger sundt, åbent og afspændt. Sang kræver energi og fysik, og vi arbejder med at bruge kroppen rigtigt og udnytte dine ressourcer.
                </p>
                <p className="text-base text-foreground/80 leading-relaxed">
                  Vi øver gehør og arbejder med at synge rent og ramme tonerne præcist samt med klang og artikulering. Rytmisk arbejder vi med, at du finder dig til rette i musikken og træner at mærke og følge pulsen.
                </p>
                <p className="text-base text-foreground/80 leading-relaxed">
                  Vi har fokus på udtryk og fortolkning. Hvad handler sangen om? Hvad er det, du gerne vil udtrykke? Vi træner din tonale rækkevidde: Hvor dybt og højt/lyst synger du, og kan vi skubbe lidt til de grænser? Der er sjove effekter at eksperimentere med, forskellige teknikker, fraseringer og klange.
                </p>
              </div>
            </div>

            <div className="bg-sand-100/70 p-6 rounded-none border border-sand-200 text-foreground/80 leading-relaxed">
              <p className="font-semibold text-black">
                Jeg underviser drenge og piger, unge, voksne og ældre, begyndere og øvede.
              </p>
              <p className="mt-2 font-serif italic text-lg text-black">
                Du er velkommen.<br />Kh Heidi
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. INDIVIDUEL SANGUNDERVISNING */}
      <section className="py-16 md:py-24 bg-white border-b border-sand-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-4xl mx-auto flex flex-col gap-8 text-left">
            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-4 border-b border-sand-200 pb-6">
              <div>
                <span className="text-xs uppercase tracking-widest text-sage-600 font-bold block mb-2">
                  Enkeltlektion
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-wide text-black">
                  Individuel Sangundervisning
                </h2>
              </div>
              <div className="text-left sm:text-right shrink-0">
                <span className="font-serif text-2xl sm:text-3xl font-bold text-black block">
                  600 kr.
                </span>
                <span className="text-xs uppercase tracking-wider text-gray-600 font-semibold">
                  60 minutter à 600 kr. pr. lektion
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-4 text-base md:text-lg text-foreground/80 leading-relaxed">
              <p>
                En enkelt sanglektion varer 60 minutter og koster 600 kr., som betales ved tilmelding. Ved afmelding skal der ringes eller sendes sms til TLF: 27794688 senest 24 timer før aftalt sangtid. Ved udeblivelse betales der 300 kr. i kompensation.
              </p>
              <p>
                Vi arbejder gradvist med grundlæggende sangteknik, hvor vi afhjælper spændinger i kæber, læber, tunge, nakke og hals, så du synger sundt og afspændt.
              </p>
            </div>

            <div className="pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-cta px-8 py-3.5 text-sm uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all shadow-md active:scale-98"
              >
                TILMELD / BOOK TID <i className="fa-solid fa-chevron-right text-[10px]" />
              </Link>
            </div>

            {/* Billede efter individuel sangundervisning */}
            <div className="mt-8 pt-8 border-t border-sand-200">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-5">
                  <div className="overflow-hidden rounded-none shadow-md border border-sand-200">
                    <img
                      src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/img-4488.jpeg"
                      alt="Solsalen - Her er der plads til dig og din stemme"
                      className="w-full aspect-[3/4] object-cover"
                    />
                  </div>
                </div>
                <div className="md:col-span-7 flex flex-col gap-4 text-left">
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-black">
                    Her er der plads til dig og din stemme
                  </h3>
                  <p className="text-base text-foreground/80 leading-relaxed">
                    Solsalen i Kunst- og Filosoficentret Solvognen danner rammen om undervisningen. Et lyst lokale med franske døre ud til Eventyrhavens grønne omgivelser, der indbyder til nærvær, ro og fordybelse.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. SANGFORLØB, OPSAMLING & AFTENSKOLEKURSUSFORLØB */}
      <section className="py-16 md:py-24 bg-sand-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-4xl mx-auto flex flex-col gap-12 text-left">
            <div>
              <span className="text-xs uppercase tracking-widest text-sage-600 font-bold block mb-2">
                Forløb & fællesskaber
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
                Sangforløb, opsamling & aftenskolekursusforløb
              </h2>
            </div>

            {/* Sangforløb */}
            <div className="bg-white p-8 md:p-10 rounded-none border border-sand-200 shadow-sm flex flex-col gap-6">
              <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 border-b border-sand-200 pb-4">
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-black">
                  Sangforløb
                </h3>
                <span className="text-base font-bold text-clay-900">
                  8 x 60 min. / 4.200 kr.
                </span>
              </div>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                Jeg anbefaler stærkt mit sangforløb på 8 x 60 min. til 7 ganges pris. Du sparer altså 600 kr. ved køb af et forløb på 8 lektioner til 4.200 kr. i alt, hvor du når i dybden med din stemmetræning og i mål med eksempelvis en særlig udfordring. Det kunne være noget sangteknisk, evt. forberedelse til en audition, optræden, arbejde med eget materiale, personlig udvikling, kreativ udfoldelse m.m.
              </p>
              <div>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full bg-cta px-8 py-3 text-sm uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all shadow-sm active:scale-98"
                >
                  TILMELD / BOOK TID <i className="fa-solid fa-chevron-right text-[10px]" />
                </Link>
              </div>
            </div>

            {/* Opsamling */}
            <div className="bg-white p-8 md:p-10 rounded-none border border-sand-200 shadow-sm flex flex-col gap-4">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-black">
                Opsamling
              </h3>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                Når du synger hos mig, kan du tilmed være med til løbende opsamling, hvor vi samles og mødes op til 8 sangere ad gangen og synger for hinanden. Det giver dig en enestående mulighed for at afprøve de sange, du arbejder med, foran et publikum af andre sangere og i undervisningens vante rammer.
              </p>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                På den måde kan du yderligere udfordre dig selv gradvist i din personlige udvikling og stemmetræning. Det er altid superhyggeligt og givende at være med til. Det er gratis at deltage i opsamling, når du er i et sangforløb hos mig, og du vil blive inviteret, når det er aktuelt.
              </p>
            </div>

            {/* Aftenskolekursusforløb */}
            <div className="bg-white p-8 md:p-10 rounded-none border border-sand-200 shadow-sm flex flex-col gap-4">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-black">
                Aftenskolekursusforløb
              </h3>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                Ud over min privatundervisning, hvor du booker et forløb eller en enkelt time ad gangen på det tidspunkt, der passer dig, udbyder jeg også begrænsede pladser på kursusforløb i foråret og i efterårs-/vinterhalvåret ved aftenskolerne FOF og AOF.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
              Hvad mine sangere siger
            </h2>
          </div>
          <ReviewSlider />
        </div>
      </section>
    </div>
  );
}
